import localforage from 'localforage';

export type WordList = string[];

export interface WordResult {
  word: string;
  distance: string;
}

export interface WordWithQuality {
  word: string;
  quality?: number;
}

export interface CustomWordlistData {
  name: string;
  words: WordWithQuality[];
  qualityLevels: number[];
  hasQualityRatings: boolean;
  uploadedAt: number;
  wordCount: number;
}

const CUSTOM_WORDLIST_KEY = 'wordfinder_custom_wordlist';

/**
 * Calculate the Levenshtein distance (edit distance) between two words
 */
export const calculateNetDistance = (word1: string, word2: string): number => {
  const len1 = word1.length;
  const len2 = word2.length;

  const dp = Array.from({ length: len1 + 1 }, () => Array(len2 + 1).fill(0));

  for (let i = 0; i <= len1; i++) {
    dp[i][0] = i;
  }
  for (let j = 0; j <= len2; j++) {
    dp[0][j] = j;
  }

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (word1[i - 1] === word2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        const insert = dp[i][j - 1] + 1;
        const remove = dp[i - 1][j] + 1;
        const replace = dp[i - 1][j - 1] + 1;
        dp[i][j] = Math.min(insert, remove, replace);
      }
    }
  }

  return dp[len1][len2];
};

/**
 * Calculate the edit distance and format it as a string showing the operations
 * Example: "(~1, +2, -1)" means 1 replacement, 2 additions, 1 deletion
 */
export const calculateEditDistance = (str1: string, str2: string): string => {
  const m = str1.length;
  const n = str2.length;
  const dp: number[][] = [];

  for (let i = 0; i <= m; i++) {
    dp[i] = [];
    for (let j = 0; j <= n; j++) {
      if (i === 0) {
        dp[i][j] = j;
      } else if (j === 0) {
        dp[i][j] = i;
      } else if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
      }
    }
  }

  let added = 0;
  let removed = 0;
  let replaced = 0;

  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && dp[i][j] === dp[i - 1][j] + 1) {
      removed++;
      i--;
    } else if (j > 0 && dp[i][j] === dp[i][j - 1] + 1) {
      added++;
      j--;
    } else {
      if (i > 0 && j > 0 && dp[i][j] === dp[i - 1][j - 1] + 1) {
        replaced++;
      }
      i--;
      j--;
    }
  }

  const results: string[] = [];
  if (replaced > 0) results.push(`~${replaced}`);
  if (added > 0) results.push(`+${added}`);
  if (removed > 0) results.push(`-${removed}`);

  return `(${results.join(', ')})`;
};

/**
 * Find all words in the word list that are within the specified distance range
 */
export const getWordDistances = (
  word: string,
  wordList: WordList,
  minLength: number,
  maxLength: number,
  minDistance: number,
  maxDistance: number,
): WordResult[] => {
  const results: WordResult[] = [];

  wordList.forEach(candidate => {
    if (candidate.length < minLength || candidate.length > maxLength || word === candidate) {
      return;
    }
    const distance = calculateNetDistance(word, candidate);
    if (distance >= minDistance && distance <= maxDistance) {
      results.push({
        word: candidate,
        distance: calculateEditDistance(word, candidate),
      });
    }
  });

  return results;
};

/**
 * Calculate the net morph size from a distance string
 * Example: "(~2, +1)" -> 3, "(+1, -1, ~1)" -> 3
 */
const calculateNetMorphSize = (distance: string): number => {
  let netSize = 0;

  // Extract numbers preceded by ~, +, or -
  const tildeMatch = distance.match(/~(\d+)/);
  const plusMatch = distance.match(/\+(\d+)/);
  const minusMatch = distance.match(/-(\d+)/);

  if (tildeMatch) netSize += parseInt(tildeMatch[1]);
  if (plusMatch) netSize += parseInt(plusMatch[1]);
  if (minusMatch) netSize += parseInt(minusMatch[1]);

  return netSize;
};

/**
 * Apply filter to results based on the filter type
 */
export const applyFilter = (results: WordResult[], filter: string): WordResult[] => {
  let filteredResults: WordResult[];

  if (filter === 'plusMinus') {
    filteredResults = results.filter(candidate => !candidate.distance.includes('~'));
  } else if (filter === 'tilde') {
    filteredResults = results.filter(candidate => !/[+-]/.test(candidate.distance));
  } else {
    filteredResults = results;
  }

  // Sort by net morph size first, then alphabetically
  return filteredResults.sort((a, b) => {
    const netSizeA = calculateNetMorphSize(a.distance);
    const netSizeB = calculateNetMorphSize(b.distance);

    if (netSizeA !== netSizeB) {
      return netSizeA - netSizeB;
    }

    // If net sizes are equal, sort alphabetically
    return a.word.localeCompare(b.word);
  });
};

/**
 * Fetch a word list from GitHub
 */
export const fetchWordList = async (url: string): Promise<WordList> => {
  const response = await fetch(url);
  const text = await response.text();
  return text.split('\n').map(x => x.trim().toLowerCase());
};

/**
 * Fetch a word list that should not be lowercased (like words_alpha.txt)
 */
export const fetchWordListPreserveCase = async (url: string): Promise<WordList> => {
  const response = await fetch(url);
  const text = await response.text();
  return text.split('\n').map(x => x.trim());
};

/**
 * Parse a custom wordlist file content
 * Supports formats like "word" or "word;quality"
 */
export const parseCustomWordlist = (
  content: string,
  filename: string,
  onProgress?: (progress: number) => void
): CustomWordlistData => {
  const lines = content.split('\n').filter(line => line.trim().length > 0);

  if (lines.length > 1_000_000) {
    throw new Error('Wordlist exceeds maximum size of 1 million lines');
  }

  const words: WordWithQuality[] = [];
  const qualitySet = new Set<number>();
  let hasQualityRatings = false;

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();
    if (!trimmedLine) return;

    // Check for "word;quality" format
    const parts = trimmedLine.split(';');
    const word = parts[0].trim().toLowerCase();

    if (word.length === 0) return;

    let quality: number | undefined;
    if (parts.length > 1) {
      const qualityStr = parts[1].trim();
      const qualityNum = parseInt(qualityStr, 10);
      if (!isNaN(qualityNum)) {
        quality = qualityNum;
        qualitySet.add(qualityNum);
        hasQualityRatings = true;
      }
    }

    words.push({ word, quality });

    // Report progress every 10,000 words
    if (onProgress && index % 10000 === 0) {
      onProgress(Math.floor((index / lines.length) * 100));
    }
  });

  if (onProgress) {
    onProgress(100);
  }

  const qualityLevels = Array.from(qualitySet).sort((a, b) => a - b);

  return {
    name: filename,
    words,
    qualityLevels,
    hasQualityRatings,
    uploadedAt: Date.now(),
    wordCount: words.length,
  };
};

/**
 * Convert CustomWordlistData to WordList, optionally filtering by minimum quality
 */
export const customWordlistToWordList = (
  customData: CustomWordlistData,
  minQuality?: number
): WordList => {
  if (!customData.hasQualityRatings || minQuality === undefined) {
    return customData.words.map(w => w.word);
  }

  // Find max quality for treating unrated words
  const maxQuality = Math.max(...customData.qualityLevels);

  return customData.words
    .filter(w => {
      const quality = w.quality ?? maxQuality;
      return quality >= minQuality;
    })
    .map(w => w.word);
};

/**
 * Save custom wordlist data to LocalForage (IndexedDB)
 */
export const saveCustomWordlist = async (data: CustomWordlistData): Promise<void> => {
  await localforage.setItem(CUSTOM_WORDLIST_KEY, data);
};

/**
 * Load custom wordlist data from LocalForage
 */
export const loadCustomWordlist = async (): Promise<CustomWordlistData | null> => {
  const data = await localforage.getItem<CustomWordlistData>(CUSTOM_WORDLIST_KEY);
  return data;
};

/**
 * Remove custom wordlist from storage
 */
export const removeCustomWordlist = async (): Promise<void> => {
  await localforage.removeItem(CUSTOM_WORDLIST_KEY);
};
